'use client';

// "Missing Required" + "Validation Issues" sidebar for the Documents tab
// workbench (design Section 6.2/6.3).
//
// Missing Required: DOCUMENT_GROUPS docs where required && !uploaded, with
// payment_proof_* (and any other requiredGroup) collapsed into ONE entry —
// satisfied if ANY ONE doc sharing that requiredGroup is uploaded.
//
// Validation Issues: one banner per uploaded estimate/invoice whose
// useAmountMismatch result has isMismatch:true (skipped checks and clean
// matches render nothing).

import type { DocDef, DocRecord, ExtractionSummary } from './types';

export interface DocumentsSidebarProps {
  documentGroups: { group: string; docs: DocDef[] }[];
  docs: DocRecord[];
  extractions: Map<string, ExtractionSummary>;
}

interface MissingItem {
  key: string; // requiredGroup key, or the doc's own key when ungrouped
  label: string;
}

function fmtAmount(n: number): string {
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function computeMissingRequired(documentGroups: DocumentsSidebarProps['documentGroups'], docs: DocRecord[]): MissingItem[] {
  const uploadedTypes = new Set(docs.map(d => d.doc_type));
  const allDocs = documentGroups.flatMap(g => g.docs);

  const missing: MissingItem[] = [];
  const seenGroups = new Set<string>();

  allDocs.forEach(doc => {
    if (doc.requiredGroup) {
      if (seenGroups.has(doc.requiredGroup)) return; // already resolved this group
      seenGroups.add(doc.requiredGroup);

      const groupDocs = allDocs.filter(d => d.requiredGroup === doc.requiredGroup);
      const satisfied = groupDocs.some(d => uploadedTypes.has(d.key));
      if (!satisfied) {
        // Use the first variant's label to represent the group.
        missing.push({ key: doc.requiredGroup, label: groupDocs[0].label });
      }
      return;
    }

    if (doc.required && !uploadedTypes.has(doc.key)) {
      missing.push({ key: doc.key, label: doc.label });
    }
  });

  return missing;
}

interface MismatchItem {
  docId: string;
  label: string;
  expected: number;
  entered: number;
}

function computeValidationIssues(
  documentGroups: DocumentsSidebarProps['documentGroups'],
  docs: DocRecord[],
  extractions: Map<string, ExtractionSummary>,
): MismatchItem[] {
  const allDocs = documentGroups.flatMap(g => g.docs);
  const labelByType = new Map(allDocs.map(d => [d.key, d.label]));

  const issues: MismatchItem[] = [];
  docs.forEach(doc => {
    const summary = extractions.get(doc.id);
    if (!summary || summary.mismatch.skipped || !summary.mismatch.isMismatch) return;
    if (summary.mismatch.expected == null || summary.mismatch.entered == null) return;
    issues.push({
      docId: doc.id,
      label: labelByType.get(doc.doc_type) ?? doc.doc_type,
      expected: summary.mismatch.expected,
      entered: summary.mismatch.entered,
    });
  });

  return issues;
}

export function DocumentsSidebar({ documentGroups, docs, extractions }: DocumentsSidebarProps) {
  const missing = computeMissingRequired(documentGroups, docs);
  const issues = computeValidationIssues(documentGroups, docs, extractions);

  return (
    <div className="flex flex-col gap-6">
      {/* Missing Required */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Missing Required</h3>
        {missing.length === 0 ? (
          <p className="text-xs text-green-600">All required documents uploaded.</p>
        ) : (
          <ul className="space-y-2">
            {missing.map(item => (
              <li key={item.key} className="flex items-start gap-2 text-xs text-gray-600">
                <span className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-400" />
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Validation Issues */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Validation Issues</h3>
        {issues.length === 0 ? (
          <p className="text-xs text-green-600">No validation issues.</p>
        ) : (
          <ul className="space-y-2">
            {issues.map(issue => (
              <li key={issue.docId} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                <p className="font-semibold">{issue.label} — amount mismatch</p>
                <p className="mt-0.5">
                  Expected ₹{fmtAmount(issue.expected)}, entered ₹{fmtAmount(issue.entered)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
